import {
  ExerciseCanvas,
  roundRect,
  drawLimb,
  drawPersonHead,
  drawCardFrame,
  drawChecklistPanel,
  drawPhasePill,
} from "@/components/we360/ExerciseCanvas";

const GREEN = "#157346";
const GREEN_LIGHT = "#E2F0E8";

export function FoodWelcomeSVG() {
  return (
    <ExerciseCanvas
      cycleDurationMs={2800}
      testId="f4-welcome-canvas"
      renderFrame={(ctx, t) => {
        const floatY = Math.sin(t * Math.PI * 2) * 4;

        drawCardFrame(ctx, {
          title: "Hábitos Alimentarios y Nutrición (FFQ)",
          subtitle: "Evalúa la frecuencia semanal de tus grupos de alimentos e hidratación",
          badgeText: "Plato Salud",
          progress: t,
          isHighlight: true,
          accentColor: GREEN,
          accentLight: GREEN_LIGHT,
        });

        // Healthy Harvard Plate diagram on left stage
        const cx = 200;
        const cy = 212 + floatY * 0.5;
        const outerR = 82;
        const innerR = 66;

        ctx.save();
        ctx.shadowColor = "rgba(45,42,36,0.08)";
        ctx.shadowBlur = 16;
        ctx.beginPath();
        ctx.arc(cx, cy, outerR, 0, Math.PI * 2);
        ctx.fillStyle = "#FFFFFF";
        ctx.fill();
        ctx.restore();

        ctx.beginPath();
        ctx.arc(cx, cy, outerR, 0, Math.PI * 2);
        ctx.strokeStyle = GREEN;
        ctx.lineWidth = 2.5;
        ctx.stroke();

        // 50% Vegetables & Fruits (left half)
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.arc(cx, cy, innerR, Math.PI / 2, (Math.PI * 3) / 2);
        ctx.closePath();
        ctx.fillStyle = "#E2F0E8";
        ctx.fill();

        // 25% Whole Grains (top right)
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.arc(cx, cy, innerR, -Math.PI / 2, 0);
        ctx.closePath();
        ctx.fillStyle = "#FFF3DC";
        ctx.fill();

        // 25% Healthy Protein (bottom right)
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.arc(cx, cy, innerR, 0, Math.PI / 2);
        ctx.closePath();
        ctx.fillStyle = "#E3F0F5";
        ctx.fill();

        // Dividing lines
        ctx.strokeStyle = "#FFFFFF";
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(cx, cy - innerR);
        ctx.lineTo(cx, cy + innerR);
        ctx.moveTo(cx, cy);
        ctx.lineTo(cx + innerR, cy);
        ctx.stroke();

        // Labels inside segments
        ctx.fillStyle = GREEN;
        ctx.font = '800 12px "Plus Jakarta Sans", sans-serif';
        ctx.fillText("50%", cx - 44, cy - 4);
        ctx.font = '600 9.5px "Plus Jakarta Sans", sans-serif';
        ctx.fillText("Vegetales", cx - 54, cy + 10);

        ctx.fillStyle = "#D97706";
        ctx.font = '800 11px "Plus Jakarta Sans", sans-serif';
        ctx.fillText("25%", cx + 14, cy - 22);
        ctx.font = '600 9px "Plus Jakarta Sans", sans-serif';
        ctx.fillText("Granos", cx + 12, cy - 10);

        ctx.fillStyle = "#00628F";
        ctx.font = '800 11px "Plus Jakarta Sans", sans-serif';
        ctx.fillText("25%", cx + 14, cy + 20);
        ctx.font = '600 9px "Plus Jakarta Sans", sans-serif';
        ctx.fillText("Proteína", cx + 10, cy + 32);

        drawChecklistPanel(ctx, {
          title: "Grupos Evaluados",
          tips: [
            { label: "Alimentos Protectores", sub: "Frutas, verduras, legumbres", active: true },
            { label: "Proteínas y Grasas", sub: "Pescado, carnes, lácteos", active: true },
            { label: "Ultraprocesados", sub: "Azúcares, frituras y sodio", active: true },
          ],
        });

        drawPhasePill(ctx, {
          label: "Piensa en tu alimentación habitual durante una semana típica",
          isHighlight: true,
        });
      }}
    />
  );
}

export function BehaviorSVG() {
  return (
    <ExerciseCanvas
      cycleDurationMs={2800}
      testId="f4-behavior-canvas"
      renderFrame={(ctx, t) => {
        const handAngle = -Math.PI / 2 + t * Math.PI * 2;

        drawCardFrame(ctx, {
          title: "Conductas y Horarios Alimentarios",
          subtitle: "Regularidad de tus comidas principales y hábitos en la mesa",
          badgeText: "Horarios",
          progress: t,
          isHighlight: t > 0.5,
          accentColor: GREEN,
          accentLight: GREEN_LIGHT,
        });

        // Circadian meal clock on left stage
        const cx = 200;
        const cy = 212;
        const r = 74;

        ctx.save();
        ctx.shadowColor = "rgba(45,42,36,0.08)";
        ctx.shadowBlur = 14;
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fillStyle = "#FFFFFF";
        ctx.fill();
        ctx.restore();

        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.strokeStyle = GREEN;
        ctx.lineWidth = 3;
        ctx.stroke();

        // Meal time badges around clock (8am Desayuno, 1pm Almuerzo, 7pm Cena)
        const meals = [
          { angle: -Math.PI * 0.75, label: "Desayuno", color: "#F9A01B" },
          { angle: -Math.PI * 0.25, label: "Almuerzo", color: GREEN },
          { angle: Math.PI * 0.35, label: "Cena", color: "#00628F" },
        ];

        meals.forEach((m) => {
          const mx = cx + Math.cos(m.angle) * (r - 22);
          const my = cy + Math.sin(m.angle) * (r - 22);
          ctx.beginPath();
          ctx.arc(mx, my, 8, 0, Math.PI * 2);
          ctx.fillStyle = m.color;
          ctx.fill();
        });

        // Clock hands
        ctx.strokeStyle = GREEN;
        ctx.lineWidth = 3.5;
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(cx + Math.cos(handAngle) * 42, cy + Math.sin(handAngle) * 42);
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(cx, cy, 6, 0, Math.PI * 2);
        ctx.fillStyle = "#2D2A24";
        ctx.fill();

        drawChecklistPanel(ctx, {
          title: "Hábitos Clave",
          tips: [
            { label: "Desayuno diario", sub: "Energía metabólica inicial", active: true },
            { label: "Horarios regulares", sub: "Evita ayunos desordenados", active: true },
            { label: "Atención plena", sub: "Comer sin pantallas", active: t > 0.5 },
          ],
        });

        drawPhasePill(ctx, {
          label: "Indica la regularidad de tus comidas y tu consumo de sal/azúcar",
          isHighlight: true,
        });
      }}
    />
  );
}

export function HydrationSVG() {
  return (
    <ExerciseCanvas
      cycleDurationMs={2600}
      testId="f4-hydration-canvas"
      renderFrame={(ctx, t) => {
        const wave = Math.sin(t * Math.PI * 2);
        const fillProgress = 0.45 + ((1 - Math.cos(t * Math.PI * 2)) / 2) * 0.4;

        drawCardFrame(ctx, {
          title: "Hidratación Diaria (Agua Pura)",
          subtitle: "Vasos de agua natural que consumes en promedio cada día",
          badgeText: "Meta: 8+",
          progress: t,
          isHighlight: fillProgress > 0.72,
          accentColor: "#00628F",
          accentLight: "#E3F0F5",
        });

        // Person holding hydration bottle / large water gauge on left stage
        const gx = 152;
        const gy = 116;
        const gw = 96;
        const gh = 186;

        roundRect(ctx, gx, gy, gw, gh, 20);
        ctx.fillStyle = "#F5FAFC";
        ctx.fill();
        ctx.strokeStyle = "#00628F";
        ctx.lineWidth = 2.5;
        ctx.stroke();

        // Water level inside glass/bottle
        const waterH = gh * fillProgress;
        const waterY = gy + gh - waterH;
        ctx.save();
        roundRect(ctx, gx + 3, gy + 3, gw - 6, gh - 6, 17);
        ctx.clip();

        ctx.fillStyle = "rgba(0, 98, 143, 0.22)";
        ctx.fillRect(gx, waterY + wave * 3, gw, waterH + 10);

        ctx.fillStyle = "rgba(0, 119, 172, 0.38)";
        ctx.fillRect(gx, waterY + 8 - wave * 3, gw, waterH + 10);
        ctx.restore();

        // Measurement ticks on bottle
        for (let i = 1; i <= 4; i++) {
          const ty = gy + gh - (gh / 5) * i;
          ctx.strokeStyle = "#00628F";
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.moveTo(gx + gw - 18, ty);
          ctx.lineTo(gx + gw - 6, ty);
          ctx.stroke();
        }

        const glasses = Math.round(fillProgress * 10);
        ctx.fillStyle = "#00628F";
        ctx.font = '800 20px "Plus Jakarta Sans", sans-serif';
        ctx.fillText(`${glasses} vasos`, gx + 14, gy + gh / 2 + 6);

        drawChecklistPanel(ctx, {
          title: "Guía de Hidratación",
          tips: [
            { label: "8 vasos / 2 Litros", sub: "Meta diaria recomendada", active: fillProgress > 0.7 },
            { label: "Agua pura natural", sub: "Sin contar gaseosas/jugos", active: true },
            { label: "Balance hídrico", sub: "Aumenta si haces ejercicio", active: true },
          ],
        });

        drawPhasePill(ctx, {
          label: "1 vaso equivale aproximadamente a 250 ml de agua pura",
          isHighlight: fillProgress > 0.7,
        });
      }}
    />
  );
}

export function F4SummarySVG({ complete }) {
  return (
    <ExerciseCanvas
      cycleDurationMs={2800}
      testId="f4-summary-canvas"
      renderFrame={(ctx, t) => {
        const pulse = (1 - Math.cos(t * Math.PI * 2)) / 2;

        drawCardFrame(ctx, {
          title: "Resumen de Evaluación Nutricional (F4)",
          subtitle: complete
            ? "¡Evaluación lista! Revisa tu perfil de calidad alimentaria"
            : "Verifica el resumen de tus hábitos alimentarios antes de enviar",
          badgeText: complete ? "FFQ Listo" : "Resumen",
          progress: complete ? 1 : t,
          isHighlight: Boolean(complete),
          accentColor: GREEN,
          accentLight: GREEN_LIGHT,
        });

        const cx = 205;
        const cy = 215;

        ctx.beginPath();
        ctx.arc(cx, cy, 68 + pulse * 6, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(21,115,70,0.10)";
        ctx.fill();

        ctx.beginPath();
        ctx.arc(cx, cy, 52, 0, Math.PI * 2);
        ctx.fillStyle = GREEN;
        ctx.fill();

        ctx.beginPath();
        ctx.arc(cx, cy, 42, 0, Math.PI * 2);
        ctx.fillStyle = "#FFFFFF";
        ctx.fill();

        ctx.fillStyle = GREEN;
        ctx.font = '800 16px "Plus Jakarta Sans", sans-serif';
        ctx.fillText("FFQ", cx - 17, cy + 6);

        drawChecklistPanel(ctx, {
          title: "Balance Nutricional",
          tips: [
            { label: "Calidad de Dieta", sub: "Puntaje global FFQ", active: true },
            { label: "Hidratación Diaria", sub: "Consumo de agua pura", active: true },
            { label: "Conductas y Horarios", sub: "Patrón metabólico diario", active: Boolean(complete) },
          ],
        });

        drawPhasePill(ctx, {
          label: complete
            ? "✓ Presiona «Enviar evaluación» para sincronizar tu perfil nutricional"
            : "Revisa tus indicadores nutricionales para finalizar",
          isHighlight: Boolean(complete),
        });
      }}
    />
  );
}
